import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as authService from '@/lib/api/services/auth'
import { adminUserService } from '@/lib/api/services/admin-user'
import type { LoginRequest, VerifyOtpRequest, AdminUser, CreateAdminUserRequest, VerifyAccountInviteRequest, ActivateAccountRequest, ResetPasswordRequest } from '@/lib/api/types'

// Query keys for React Query caching
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const
}

// Login hook
export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: authKeys.all })
      }
    },
    onError: (error) => {
      console.error('Login failed:', error)
    }
  })
}

// OTP verification hook
export function useVerifyOtp() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: VerifyOtpRequest) => authService.verifyOtp(credentials),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: authKeys.all })
      }
    },
    onError: (error) => {
      console.error('OTP verification failed:', error)
    }
  })
}

// Logout hook
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      queryClient.clear()
    },
    onError: (error) => {
      console.error('Logout failed:', error)
      queryClient.clear()
    }
  })
}

// Get current user hook - fetches real-time data from API
export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: async (): Promise<AdminUser> => {
      // Get current user ID from sessionStorage to identify which user in the array
      const storedUser = authService.getCurrentUser()
      if (!storedUser) {
        throw new Error('No authenticated user')
      }
      
      // Fetch real-time profile data from API
      const response = await adminUserService.getCurrentUserProfile()
      
      if (!response.success || !response.data || !Array.isArray(response.data)) {
        throw new Error('Failed to fetch user profile')
      }
      
      // Find the current user in the array by matching userId
      const currentUserId = storedUser.userProfile?.userId
      const userProfile = response.data.find(
        (user) => user.userId === currentUserId
      )
      
      if (!userProfile) {
        // Fallback to stored user if not found in API response
        console.warn('Current user not found in API response, using cached user')
        return storedUser
      }
      
      // Transform API response (flat structure) to AdminUser format
      const adminUser: AdminUser = {
        adminId: userProfile.userId,
        userProfile: {
          userId: userProfile.userId,
          firstName: userProfile.firstName || '',
          lastName: userProfile.lastName || '',
          emailAddress: userProfile.emailAddress || ''
        },
        userRole: userProfile.userRole || '',
        isDefaultPassword: userProfile.isDefaultPassword || false,
        hasActivatedAccount: userProfile.hasActivatedAccount || false,
        isTwoFactorEnabled: userProfile.isTwoFactorEnabled || false,
        otpChannel: userProfile.otpChannel
      }
      
      // Update sessionStorage and cache with fresh data
      authService.updateCurrentUser(adminUser)
      
      return adminUser
    },
    enabled: authService.isAuthenticated(),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: false
  })
}

// Main auth hook with all auth state
export function useAuth() {
  const { data: user, isLoading, error } = useQuery({
    queryKey: authKeys.user(),
    queryFn: (): AdminUser => {
      const user = authService.getCurrentUser()
      const isAuth = authService.isAuthenticated()

      if (!isAuth || !user) {
        throw new Error('No authenticated user')
      }
      return user
    },
    retry: false,
    staleTime: 0, // Always check fresh
    refetchOnMount: true,
    refetchOnWindowFocus: true
  })

  return {
    user,
    isAuthenticated: authService.isAuthenticated(),
    isLoading,
    error,
    hasRole: (role: string) => authService.hasRole(role)
  }
}

// Invite admin user hook
export function useInviteAdminUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAdminUserRequest) => authService.inviteAdminUser(data),
    onSuccess: (response) => {
      if (response.success) {
        // Invalidate admin users list to refresh
        queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      }
    },
    onError: (error) => {
      console.error('Failed to invite admin user:', error)
    }
  })
}

// Verify account invite hook
export function useVerifyAccountInvite() {
  return useMutation({
    mutationFn: (data: VerifyAccountInviteRequest) => authService.verifyAccountInvite(data),
    onSuccess: (response) => {
      if (response.success) {
        // Account invite verified successfully
      }
    },
    onError: (error) => {
      console.error('Failed to verify account invite:', error)
    }
  })
}

// Activate account hook
export function useActivateAccount() {
  return useMutation({
    mutationFn: (data: ActivateAccountRequest) => authService.activateAccount(data),
    onSuccess: (response) => {
      if (response.success) {
        // Account activated successfully
      }
    },
    onError: (error) => {
      console.error('Failed to activate account:', error)
    }
  })
}

// Forgot password hook
export function useForgotPassword() {
  return useMutation({
    mutationFn: (emailAddress: string) => authService.forgotPassword(emailAddress),
    onSuccess: (response) => {
      if (response.success) {
        // Password reset OTP sent successfully
      }
    },
    onError: (error) => {
      console.error('Failed to send password reset OTP:', error)
    }
  })
}

// Reset password hook
export function useResetPassword() {
  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => authService.resetPassword(data),
    onSuccess: (response) => {
      if (response.success) {
        // Password reset successfully
      }
    },
    onError: (error) => {
      console.error('Failed to reset password:', error)
    }
  })
}